import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { customerAPI } from '@/services/customerAPI';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Search,
  Eye,
  AlertCircle,
  XCircle,
  Filter,
  Calendar,
  DollarSign
} from 'lucide-react';
import { formatCurrency } from '@/utils/searchValidation';
import type { SearchType } from '@/types';

const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
  validate: 'Identity Validation',
  enrich: 'Data Enrichment',
  enrich_validate: 'Enrich + Validate',
  scoring: 'Scoring Only',
  scoring_enrich: 'Scoring + Enrichment',
};

export const SearchHistoryPage: React.FC = () => {
  const { user } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<SearchType | 'all'>('all');

  const { data: searches, isLoading, error } = useQuery({
    queryKey: ['search-history', user?.userId],
    queryFn: () => customerAPI.getSearchHistory(user!.userId, 100),
    enabled: !!user?.userId,
  });

  const filteredSearches = searches?.filter((search) => {
    const fullName = `${search.searchRequest?.firstName || 'Unknown'} ${search.searchRequest?.lastName || 'Person'}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || search.searchType === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleViewSearch = (searchId: string) => {
    // Navigate to search results page
    navigate(`/search/results/${searchId}`);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-10 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Failed to Load Search History</h2>
          <p className="text-slate-600 mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Search History</h1>
        <p className="text-sm text-slate-600">
          View and revisit your previous search results
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-collectability-blue focus:border-transparent"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-600" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as SearchType | 'all')}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-collectability-blue focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="validate">Identity Validation</option>
              <option value="enrich">Data Enrichment</option>
              <option value="enrich_validate">Enrich + Validate</option>
              <option value="scoring">Scoring Only</option>
              <option value="scoring_enrich">Scoring + Enrichment</option>
            </select>
          </div>
        </div>

        {filteredSearches && filteredSearches.length > 0 && (
          <div className="mt-3 text-xs text-slate-600">
            Showing {filteredSearches.length} of {searches?.length || 0} searches
          </div>
        )}
      </Card>

      {/* Search List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-3 animate-pulse">
              <div className="h-20 bg-slate-100 rounded" />
            </Card>
          ))}
        </div>
      ) : filteredSearches && filteredSearches.length > 0 ? (
        <div className="space-y-3">
          {filteredSearches.map((search) => (
            <Card
              key={search.id}
              className="p-3 transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                {/* Search Info */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${
                    search.hasResults ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    {search.hasResults ? (
                      <Search className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-slate-600" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-slate-900 mb-0.5">
                      {search.searchRequest?.firstName || 'Unknown'} {search.searchRequest?.lastName || 'Person'}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 mb-1">
                      <span>{SEARCH_TYPE_LABELS[search.searchType]}</span>
                      <span>•</span>
                      <span className="flex items-center gap-0.5">
                        <DollarSign className="w-3 h-3" />
                        {formatCurrency(search.cost || 0)}
                      </span>
                      {search.searchRequest?.amountToCollect && (
                        <>
                          <span>•</span>
                          <span>Debt: {formatCurrency(search.searchRequest.amountToCollect)}</span>
                        </>
                      )}
                    </div>

                    {/* Additional Search Details */}
                    <div className="text-xs text-slate-500">
                      {search.searchRequest?.address && (
                        <span>
                          {search.searchRequest.address}
                          {search.searchRequest.city && `, ${search.searchRequest.city}`}
                          {search.searchRequest.state && `, ${search.searchRequest.state}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex flex-col gap-0.5 text-xs text-slate-600 lg:text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Calendar className="w-3 h-3" />
                    {new Date(search.timestamp).toLocaleDateString()}
                  </div>
                  <div className={`text-xs font-medium ${
                    search.hasResults ? 'text-green-600' : 'text-slate-500'
                  }`}>
                    {search.hasResults ? 'Match Found' : 'No Hit'}
                  </div>
                </div>

                {/* View Button */}
                <div className="flex items-center gap-2 lg:flex-shrink-0">
                  {search.hasResults ? (
                    <Button
                      onClick={() => handleViewSearch(search.searchId)}
                      className="gap-1.5 h-8 text-xs"
                      size="sm"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View Results
                    </Button>
                  ) : (
                    <Button variant="outline" disabled size="sm" className="h-8 text-xs">
                      No Results
                    </Button>
                  )}
                </div>
              </div>

              {/* Show scores based on operation type */}
              {search.hasResults && search.result && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="flex items-center gap-3 text-xs">
                    {/* Scoring operations: show collectability score and band */}
                    {(search.searchType === 'scoring' || search.searchType === 'scoring_enrich') && (
                      <>
                        <div>
                          <span className="text-slate-600">Score: </span>
                          <span className="font-semibold text-slate-900">
                            {search.result.collectabilityScore}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-600">Band: </span>
                          <span className={`font-semibold ${
                            search.result.collectabilityBand === 'High' ? 'text-green-600' :
                            search.result.collectabilityBand === 'Medium' ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                            {search.result.collectabilityBand}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Validation operations: show identity confidence and fraud risk */}
                    {(search.searchType === 'validate' || search.searchType === 'enrich_validate') && search.result.primaryVerified && (
                      <>
                        <div>
                          <span className="text-slate-600">Identity: </span>
                          <span className="font-semibold text-slate-900">
                            {search.result.primaryVerified.identityConfidenceScore}%
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-600">Fraud Risk: </span>
                          <span className={`font-semibold ${
                            search.result.primaryVerified.fraudRisk < 30 ? 'text-green-600' :
                            search.result.primaryVerified.fraudRisk < 70 ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                            {search.result.primaryVerified.fraudRisk}/100
                          </span>
                        </div>
                      </>
                    )}

                    {/* Pure enrich: no scores to display */}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-10 text-center">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-900 mb-2">
            {searchQuery || typeFilter !== 'all' ? 'No Searches Found' : 'No Search History'}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            {searchQuery || typeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Perform your first search to get started'}
          </p>
          {!searchQuery && typeFilter === 'all' && (
            <Button onClick={() => navigate('/search')} size="sm" className="h-8 text-xs">
              Perform a Search
            </Button>
          )}
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-3 mt-6 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-xs font-semibold text-slate-900 mb-1">
              Search History Retention
            </h3>
            <p className="text-xs text-blue-800">
              Your search history and results are stored indefinitely and can be accessed anytime.
              You can revisit past searches without additional charges.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

